import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from "react";
import { logger } from "@/lib/logger";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
}

export type DeviceType = "ios" | "android" | "desktop";

export interface PWAContextValue {
  isInstalled: boolean;
  canInstall: boolean;
  deviceType: DeviceType;
  triggerInstall: () => Promise<boolean>;
  getInstructions: () => { title: string; steps: string[] };
}

const PWAContext = createContext<PWAContextValue | null>(null);

export function PWAProvider({ children }: { children: React.ReactNode }) {
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [deviceType, setDeviceType] = useState<DeviceType>("desktop");

  useEffect(() => {
    // Detect device type once
    const userAgent = navigator.userAgent.toLowerCase();

    if (/ipad|iphone|ipod/.test(userAgent) && !(window as any).MSStream) {
      setDeviceType("ios");
    } else if (/android/.test(userAgent)) {
      setDeviceType("android");
    } else {
      setDeviceType("desktop");
    }

    // Capture the beforeinstallprompt event globally
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e as BeforeInstallPromptEvent;
      setCanInstall(true);
      logger.debug("[PWA] beforeinstallprompt captured");
    };

    const handleAppInstalled = () => {
      logger.debug("[PWA] App installed");
      localStorage.setItem('pwa-installed', 'true');
      setIsInstalled(true);
      setCanInstall(false);
      deferredPromptRef.current = null;
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    // Detect if already installed (PWA standalone mode or localStorage)
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
    const isIOSStandalone = (window.navigator as any).standalone;
    const wasInstalled = localStorage.getItem('pwa-installed') === 'true';

    if (isStandalone || isIOSStandalone || wasInstalled) {
      setIsInstalled(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const triggerInstall = useCallback(async (): Promise<boolean> => {
    const prompt = deferredPromptRef.current;
    if (!prompt) {
      logger.debug("[PWA] No deferred prompt available");
      return false;
    }

    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      logger.debug("[PWA] User choice", { outcome: choice.outcome });

      if (choice.outcome === "accepted") {
        deferredPromptRef.current = null;
        setCanInstall(false);
        return true;
      }
    } catch (error) {
      console.error("[PWA] Error during install:", error);
    }

    return false;
  }, []);

  const getInstructions = useCallback(() => {
    switch (deviceType) {
      case "ios":
        return {
          title: "settings.appInstallation.iosTitle",
          steps: [
            "settings.appInstallation.iosStep1",
            "settings.appInstallation.iosStep2",
            "settings.appInstallation.iosStep3",
          ],
        };
      case "android":
        return {
          title: "settings.appInstallation.androidTitle",
          steps: [
            "settings.appInstallation.androidStep1",
            "settings.appInstallation.androidStep2",
            "settings.appInstallation.androidStep3",
          ],
        };
      default:
        return {
          title: "settings.appInstallation.desktopTitle",
          steps: [
            "settings.appInstallation.desktopStep1",
            "settings.appInstallation.desktopStep2",
            "settings.appInstallation.desktopStep3",
          ],
        };
    }
  }, [deviceType]);

  const value = useMemo<PWAContextValue>(
    () => ({
      isInstalled,
      canInstall,
      deviceType,
      triggerInstall,
      getInstructions,
    }),
    [isInstalled, canInstall, deviceType, triggerInstall, getInstructions]
  );

  return <PWAContext.Provider value={value}>{children}</PWAContext.Provider>;
}

export function usePWAContext(): PWAContextValue {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error("usePWAContext must be used within a PWAProvider");
  }
  return context;
}
